import {
  DashboardActiveAnomaliesEnum,
  DashboardAnomaliesNamesEnum,
  DashboardFilterName,
  DashboardFilterType,
  DashboardInstallationData,
  DashboardInstallationsFilterNames,
  DashboardInstallationsFilterType,
  DashboardInstallationsStatusEnum,
  DashboardInstallationsTile,
  DashboardInstallationTimeBasedEnum,
  DashboardPaidPackagesEnum,
  ViError,
} from '@vi-iot/client';
import { Tab } from '@vi-iot/ui';
import { Observable } from 'rxjs';
import { StatusValue } from '../app.state.models';

export enum DashboardTileType {
  InstallationsNotRegistered = 'Installations not registered',
  InstallationsWithFault = 'Installations with fault',
  ContractsEndingSoon = 'Contracts ending soon',
  DevicesWithLowWifi = 'Devices with low Wifi',
  LastConnectedSystems = 'Last connected systems',
  UpcomingMaintenanceForInstallations = 'Upcoming maintenance for installations',
  InstallationsWithAnomalies = 'Installations with anomalies',
  InstallationsWithoutHEMS = 'Installations without HEMS',
  InstallationsWithoutSRC = 'Installations without SRC',
}

export enum DashboardStoreStatusType {
  Initializing,
  ErrorTile,
  ErrorTab,
  EmptyTile,
  EmptyTab,
  Done,
}

export enum InstallationWithFaultTabsType {
  All = 'all',
  Error = 'error',
  Locked = 'locked',
}

export enum InstallationWithAnomaliesTabsType {
  All = 'all',
  Information = 'information',
  Warning = 'warning',
}

export enum LastConnectedSystemTabsType {
  Today = 'today',
  Yesterday = 'yesterday',
  Last7Days = 'last7Days',
  Last30Days = 'last30Days',
  LastYear = 'lastYear',
}

export type DashboardTileTabValues =
  | InstallationWithFaultTabsType
  | InstallationWithAnomaliesTabsType
  | LastConnectedSystemTabsType;

export interface TilesOrderState extends StatusValue<DashboardTileOrder[]> {
  reloadGrid: boolean;
}

export interface DashboardInstallationState extends StatusValue<DashboardInstallationData[]> {
  installationsCount: number;
  canFetch: boolean;
  initializing?: boolean;
}

export interface InstallationsWithFaultState {
  [InstallationWithFaultTabsType.All]: DashboardInstallationState;
  [InstallationWithFaultTabsType.Error]: DashboardInstallationState;
  [InstallationWithFaultTabsType.Locked]: DashboardInstallationState;
}

export interface LastConnectedSystemState {
  [LastConnectedSystemTabsType.Today]: DashboardInstallationState;
  [LastConnectedSystemTabsType.Yesterday]: DashboardInstallationState;
  [LastConnectedSystemTabsType.Last7Days]: DashboardInstallationState;
  [LastConnectedSystemTabsType.Last30Days]: DashboardInstallationState;
  [LastConnectedSystemTabsType.LastYear]: DashboardInstallationState;
}

export interface InstallationsWithAnomalies {
  [InstallationWithAnomaliesTabsType.All]: DashboardInstallationState;
  [InstallationWithAnomaliesTabsType.Warning]: DashboardInstallationState;
  [InstallationWithAnomaliesTabsType.Information]: DashboardInstallationState;
}

export interface DashboardTileOrder {
  type: DashboardTileType;
  visible: boolean;
  concealed: boolean;
}

export interface DashboardTileTab extends Tab {
  dashboardInstallationState$: Observable<DashboardInstallationState>;
  emptyDataBannerText: string;
}

export interface InstallationsForMultipleTileCompletePayload {
  tileType: DashboardTileType;
  installations: DashboardInstallationData[];
  tab?: string;
  maxItemsPerFetch: number;
  error: ViError;
}

export interface DashboardSelectedTab {
  tileType: DashboardTileType;
  selectedTabValue: string;
}

export const defaultDashboardTilesOrder: DashboardTileOrder[] = [
  {
    // order 1
    type: DashboardTileType.InstallationsWithFault,
    visible: true,
    concealed: false,
  },
  {
    // order 2
    type: DashboardTileType.ContractsEndingSoon,
    visible: true,
    concealed: false,
  },
  {
    // order 3
    type: DashboardTileType.DevicesWithLowWifi,
    visible: true,
    concealed: false,
  },
  {
    // order 4
    type: DashboardTileType.LastConnectedSystems,
    visible: true,
    concealed: false,
  },
  {
    // order 5
    type: DashboardTileType.UpcomingMaintenanceForInstallations,
    visible: true,
    concealed: false,
  },
  {
    // order 6
    type: DashboardTileType.InstallationsWithAnomalies,
    visible: true,
    concealed: false,
  },
  {
    // order 7
    type: DashboardTileType.InstallationsWithoutHEMS,
    visible: true,
    concealed: false,
  },
  {
    // order 8
    type: DashboardTileType.InstallationsWithoutSRC,
    visible: true,
    concealed: false,
  },
];

export const dashboardTileTypeToDashboardTileObjectMapper: {
  [key: string]: DashboardInstallationsTile;
} = {
  [DashboardTileType.InstallationsWithoutHEMS]: {
    storeState: 'installationsWithoutHems',
    filter: {
      filterName: DashboardInstallationsFilterNames.HemsNotActivated,
      type: DashboardInstallationsFilterType.Toggle,
      values: true,
    },
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.HemsNotActivated,
        label: DashboardFilterName.HemsNotActivated,
        type: DashboardFilterType.Toggle,
        value: true,
      },
    ],
  },
  [DashboardTileType.UpcomingMaintenanceForInstallations]: {
    storeState: 'upcomingMaintenanceForInstallations',
    filter: {
      filterName: DashboardInstallationsFilterNames.AggregatedStatus,
      type: DashboardInstallationsFilterType.Complex,
      values: [DashboardInstallationsStatusEnum.Maintenance],
    },
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.AggregatedStatus,
        label: DashboardInstallationsStatusEnum.Maintenance,
        type: DashboardFilterType.Complex,
        value: DashboardInstallationsStatusEnum.Maintenance,
      },
    ],
  },
  [DashboardTileType.InstallationsWithFault]: {
    storeState: 'installationsWithFault',
    tabs: [
      {
        value: InstallationWithFaultTabsType.All,
        storeState: InstallationWithFaultTabsType.All,
        filter: {
          filterName: DashboardInstallationsFilterNames.AggregatedStatus,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardInstallationsStatusEnum.Error],
          modifier: DashboardTileType.InstallationsWithFault,
        },
        showCounter: true,
      },
      {
        value: InstallationWithFaultTabsType.Error,
        storeState: InstallationWithFaultTabsType.Error,
        filter: {
          filterName: DashboardInstallationsFilterNames.AggregatedStatus,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardInstallationsStatusEnum.Error],
          modifier: DashboardTileType.InstallationsWithFault,
        },
        showCounter: true,
      },
      {
        value: InstallationWithFaultTabsType.Locked,
        storeState: InstallationWithFaultTabsType.Locked,
        filter: {
          filterName: DashboardInstallationsFilterNames.AggregatedStatus,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardInstallationsStatusEnum.Locked],
          modifier: DashboardTileType.InstallationsWithFault,
        },
        showCounter: true,
      },
    ],
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.AggregatedStatus,
        label: DashboardInstallationsStatusEnum.Error,
        type: DashboardFilterType.Complex,
        value: DashboardInstallationsStatusEnum.Error,
      },
    ],
  },
  [DashboardTileType.LastConnectedSystems]: {
    storeState: 'lastConnectedSystems',
    tabs: [
      {
        value: LastConnectedSystemTabsType.Today,
        storeState: LastConnectedSystemTabsType.Today,
        filter: {
          filterName: DashboardInstallationsFilterNames.RecentlyConnected,
          type: DashboardInstallationsFilterType.TimeBased,
          values: DashboardInstallationTimeBasedEnum.Today,
        },
        showCounter: true,
      },
      {
        value: LastConnectedSystemTabsType.Yesterday,
        storeState: LastConnectedSystemTabsType.Yesterday,
        filter: {
          filterName: DashboardInstallationsFilterNames.RecentlyConnected,
          type: DashboardInstallationsFilterType.TimeBased,
          values: DashboardInstallationTimeBasedEnum.Yesterday,
        },
        showCounter: true,
      },
      {
        value: LastConnectedSystemTabsType.Last7Days,
        storeState: LastConnectedSystemTabsType.Last7Days,
        filter: {
          filterName: DashboardInstallationsFilterNames.RecentlyConnected,
          type: DashboardInstallationsFilterType.TimeBased,
          values: DashboardInstallationTimeBasedEnum.Last7Days,
        },
        showCounter: true,
      },
      {
        value: LastConnectedSystemTabsType.Last30Days,
        storeState: LastConnectedSystemTabsType.Last30Days,
        filter: {
          filterName: DashboardInstallationsFilterNames.RecentlyConnected,
          type: DashboardInstallationsFilterType.TimeBased,
          values: DashboardInstallationTimeBasedEnum.Last30Days,
        },
        showCounter: true,
      },
      {
        value: LastConnectedSystemTabsType.LastYear,
        storeState: LastConnectedSystemTabsType.LastYear,
        filter: {
          filterName: DashboardInstallationsFilterNames.RecentlyConnected,
          type: DashboardInstallationsFilterType.TimeBased,
          values: DashboardInstallationTimeBasedEnum.LastYear,
        },
        showCounter: true,
      },
    ],
  },
  [DashboardTileType.InstallationsWithAnomalies]: {
    storeState: 'installationsWithAnomalies',
    tabs: [
      {
        value: InstallationWithAnomaliesTabsType.All,
        storeState: InstallationWithAnomaliesTabsType.All,
        filter: {
          filterName: DashboardInstallationsFilterNames.ActiveAnomalies,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardActiveAnomaliesEnum.Warning, DashboardActiveAnomaliesEnum.Information],
        },
        showCounter: true,
      },
      {
        value: InstallationWithAnomaliesTabsType.Warning,
        storeState: InstallationWithAnomaliesTabsType.Warning,
        filter: {
          filterName: DashboardInstallationsFilterNames.ActiveAnomalies,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardActiveAnomaliesEnum.Warning],
        },
        showCounter: true,
      },
      {
        value: InstallationWithAnomaliesTabsType.Information,
        storeState: InstallationWithAnomaliesTabsType.Information,
        filter: {
          filterName: DashboardInstallationsFilterNames.ActiveAnomalies,
          type: DashboardInstallationsFilterType.Complex,
          values: [DashboardActiveAnomaliesEnum.Information],
        },
        showCounter: true,
      },
    ],
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.ActiveAnomalies,
        label: DashboardActiveAnomaliesEnum.Information,
        type: DashboardFilterType.Complex,
        value: DashboardActiveAnomaliesEnum.Information,
      },
      {
        filterName: DashboardFilterName.ActiveAnomalies,
        label: DashboardActiveAnomaliesEnum.Warning,
        type: DashboardFilterType.Complex,
        value: DashboardActiveAnomaliesEnum.Warning,
      },
    ],
    withQuickView: true,
  },
  [DashboardTileType.InstallationsWithoutSRC]: {
    storeState: 'installationsWithoutSRC',
    filter: {
      filterName: DashboardInstallationsFilterNames.SrcNotActivated,
      type: DashboardInstallationsFilterType.Toggle,
      values: true,
    },
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.SrcNotActivated,
        label: DashboardFilterName.SrcNotActivated,
        type: DashboardFilterType.Toggle,
        value: true,
      },
    ],
  },
  [DashboardTileType.ContractsEndingSoon]: {
    storeState: 'contractsEndingSoon',
    filter: {
      filterName: DashboardInstallationsFilterNames.PaidPackages,
      type: DashboardInstallationsFilterType.Complex,
      values: [DashboardPaidPackagesEnum.Pro, DashboardPaidPackagesEnum.Plus],
    },
  },
  [DashboardTileType.DevicesWithLowWifi]: {
    storeState: 'devicesWithLowWifi',
    filter: {
      filterName: DashboardInstallationsFilterNames.AnomaliesNames,
      type: DashboardInstallationsFilterType.Complex,
      values: [DashboardAnomaliesNamesEnum.ConnectionQualityIndicatorCalculation],
    },
    kanbanBoardFilter: [
      {
        filterName: DashboardFilterName.AnomaliesNames,
        label: DashboardAnomaliesNamesEnum.ConnectionQualityIndicatorCalculation,
        type: DashboardFilterType.Complex,
        value: DashboardAnomaliesNamesEnum.ConnectionQualityIndicatorCalculation,
      },
    ],
  },
};
